<template>
  <div class='vulnerability-assessment-issue-body'>
    <span class="description-block" v-if='issue.description'>
      <div class="data-title">{{$t('detailed.issue.description.title')}}</div>
      <div class="text-container">
        {{description}}
      </div>
    </span>
    <div class="asset-block">
      <div class="data-title">{{$t('detailed.issue.asset.title')}}</div>
      <div class="text-container">
        {{asset}}
      </div>
    </div>
    <div class="tld-block">
      <div class="data-title">{{$t('detailed.issue.tld.title')}}</div>
      <div class="text-container">
        {{tld}}
      </div>
    </div>
    <div class='cvss-block scoring-details-column' v-if='cvssScore'>
      <div class="data-title">{{$t('issueDataPopup.technologies.cve.cvss')}}</div>
      <div class='text-container cvss-score' :style="cvssStyle">{{ cvssScore }}</div>
    </div>
    <div class='epss-block scoring-details-column' v-if='epssScore'>
      <div class="data-title">{{$t('issueDataPopup.va.epss')}}</div>
      <div class='text-container cve-epss'>
        <epss-score :value='epssFormattedScore'/>
      </div>
    </div>
    <div class="technical-block" v-if='displayTechnicalDataSection'>
      <div class="data-title">{{$t('issueDataPopup.technical.data')}}</div>
      <div v-if='urls && urls.length'>
        <div class='data-for-url-section' v-for='(url, index) in urls' :key='index'>
          <div class='details-column technical-data'>
            <div class='url-section'>
              <div class="data-sub-title">{{$t('issueDataPopup.url')}}</div>
              <div class='value'>
                <div class='data-title option'>
                  <span class="material-icons bullet">
                    lens_blur
                  </span>
                </div>
                <a :href='url' target="_blank"> {{url}} </a>
              </div>
            </div>
          </div>
          <div class="details-column technical-data" v-if='params[url].length > 0 && params[url][0].length'>
            <div class="data-sub-title">{{$t('issueDataPopup.param')}}</div>
            <div class='url-section' v-for='(param, index) in params[url]' :key='index'>
              <div class='value'>
                <div class='data-title option'>
                    <span class="material-icons bullet">
                      lens_blur
                    </span>
                </div>
                <span class='text-container'> {{ param }} </span>
              </div>
            </div>
          </div>
          <div class="details-column technical-data" v-if='evidences[url].length'>
            <div class="data-sub-title">{{$t('issueDataPopup.evidence')}}</div>
            <div class='url-section' v-for='(evidence, index) in evidences[url]' :key='index'>
              <div class='value'>
                <div class='data-title option'>
                    <span class="material-icons bullet">
                      lens_blur
                    </span>
                </div>
                <div class='text-container'> {{evidence}} </div>
              </div>
            </div>
          </div>
          <v-divider class='url-divider'/>
        </div>
      </div>
    </div>
    <span class="solution-block" v-if='solutions.length'>
      <div class="data-title">{{$t('detailed.issue.mitigation.title')}}</div>
        <div v-for='(solution, index) in solutions' :key='index'>
         <div class='mitigation-option'>
          <div class='data-title option'>
            <span class="material-icons bullet">
              lens_blur
            </span>
          </div>
         <div class='text-container'>{{solution}}</div>
        </div>
      </div>
    </span>
    <span class="reference-block" v-if='reference.length'>
          <div class="data-title">{{$t('detailed.issue.reference.title')}}</div>
          <div class='text-container' v-for='(reference, index) in reference' :key='index'>
            <div class='url-section'>
              <div class='data-title option'>
                <span class="material-icons bullet">
                  lens_blur
                </span>
              </div>
              <a :href="reference" target="_blank"> {{ reference }} </a>
            </div>
          </div>
        </span>
  </div>
</template>

<script>

import EpssScore from '@/components/common/EpssScore.vue';

export default {
  name: 'VulnerabilityAssessmentIssueBody',
  props: {
    issue: {
      type: Object,
      required: true,
    },
  },
  components: {
    EpssScore,
  },
  computed: {
    cvssStyle() {
      return {
        ...this.color,
        backgroundColor: this.color['background-color'],
        fontSize: '14px',
        width: '37px',
        height: '25px',
        borderRadius: '3px',
      };
    },
    color() {
      const severity = this.cvssScore;
      if (severity >= 9 && severity <= 10) {
        return {color: '#E0255E', 'background-color': '#E0255030'};
      }
      if (severity >= 7 && severity <= 8.9) {
        return {color: '#FF7F00', 'background-color': '#FF7F0030'};
      }
      if (severity >= 4 && severity <= 6.9) {
        return {color: '#2459d5', 'background-color': '#2459d530'};
      }
      return {color: '#2BAF69', 'background-color': '#2BAF6030'};
    },
    params() {
      const params = {};
      this.urls.forEach(url => {
        params[url] = this.specificFindingsData?.[url]?.param || [];
      });
      return params;
    },
    evidences() {
      const evidences = {};
      this.urls.forEach(url => {
        evidences[url] = this.specificFindingsData?.[url]?.evidence || [];
      });
      return evidences;
    },
    cvss2() {
      return this.issue.metaData?.findingsData?.cvss2?.length > 0 ? this.issue.metaData?.findingsData?.cvss2 : null;
    },
    cvss3() {
      return this.issue.metaData?.findingsData?.cvss3?.length > 0 ? this.issue.metaData?.findingsData?.cvss3 : null;
    },
    cvssScore() {
      return this.cvss3 || this.cvss2 || null;
    },
    epssScore() {
      return this.issue.metaData?.findingsData?.epss || null;
    },
    epssFormattedScore() {
      let epss = this.epssScore || 0;
      epss = Math.round(((epss * 100) + Number.EPSILON) * 100) / 100;
      return epss === 0 ? '' : epss;
    },
    asset() {
      return this.issue.asset;
    },
    tld() {
      return this.issue.tld;
    },
    reference() {
      const reference = this.issue.metaData?.findingsData?.reference;
      if (reference) {
        if (typeof reference === 'string') {
          return [reference];
        }
        return reference;
      }
      return [];
    },
    description() {
      return this.issue.metaData?.findingsData?.description || this.issue.description;
    },
    solutions() {
      return [...this.issue.metaData?.findingsData?.solution || []]?.filter(solution => solution?.length > 0 && solution !== 'N/A') || [];
    },
    specificFindingsData() {
      return this.issue.metaData?.specificFindingsData || {};
    },
    urls() {
      if (this.issue.metaData?.targets?.length) {
        if (this.issue.metaData.targets.length > 5) {
          return this.issue.metaData.targets.slice(0, 5);
        }
        return this.issue.metaData.targets;
      }
      if (this.issue.metaData?.findingsData?.url) {
        if (this.issue.metaData?.findingsData?.url.length > 5) {
          return this.issue.metaData?.findingsData?.url.slice(0, 5);
        }
        return this.issue.metaData?.findingsData?.url;
      }
      return [];
    },
    displayTechnicalDataSection() {
      return this.urls.length || Object.keys(this.evidences).some(url => this.evidences[url].length) || Object.keys(this.params).some(url => this.params[url].length);
    },
  },
};
</script>

<style lang='scss' scoped>
@import '~@/assets/styles/design-system';

.vulnerability-assessment-issue-body {
  .text-container {
    white-space: pre-line;
  }
  .rest-part {
    max-height: rem(610);
    overflow-y: auto;
    scrollbar-width: thin;
    padding-left: $space-15;
    padding-top: $space-8;
    padding-bottom: $space-15;
    background-image: url("../../assets/half-cynergy-logo.svg");
    background-size: contain;
    background-position: right;
  }
  .description-block {
    margin-bottom: $space-5;
    display: block;
    width: rem(650);
    .text-container::first-letter {
      text-transform: capitalize;
    }
    .text-container {
      padding-right: $space-15;
      font-size: $font-size-base;
      letter-spacing: 0;
      line-height: rem(18);
    }
  }
  .solution-block {
    display: block;
    padding-bottom: $space-5;
    width: rem(650);
    .bullet {
      flex: 1;
    }
    .text-container {
      padding-left: rem(5);
      padding-right: $space-15;
      color: var(--text-primary);
      font-size: $font-size-base;
      letter-spacing: 0;
      line-height: rem(18);
      padding-bottom: $space-4;
    }
    .mitigation-option {
      display: flex;
    }
    .material-icons {
      font-size: rem(15);
      opacity: 0.5;
      margin-top: rem(3);
    }
  }
  .reference-block {
    padding-bottom: $space-5;
    display: block;
    width: rem(650);
    .text-container {
      padding-right: $space-15;
      color: var(--text-primary);
      font-size: $font-size-base;
      letter-spacing: 0;
      line-height: rem(18);
      padding-bottom: $space-4;
    }
    .url-section {
      display: flex;
      flex-direction: column;
      .value {
        display: flex;
        .bullet {
          padding-right: rem(5);
        }
        .material-icons {
          font-size: rem(15);
          opacity: 0.5;
          margin-top: rem(1);
        }
      }
    }
  }
  .technical-block {
    margin-bottom: $space-5;
    display: block;
    width: rem(650);
    .data-for-url-section {
      .url-divider {
        width: rem(250);
        margin-bottom: rem(18);
      }
      .technical-data {
        padding-top: $space-1;
      }
      .data-sub-title {
        font-size: $font-size-xs;
        color: var(--text-secondary);
        letter-spacing: 0.8px;
        line-height: rem(10);
        text-transform: uppercase;
        margin-bottom: $space-1;
      }
    }
    .url-section {
      .value {
        display: flex;
        .bullet {
          padding-right: rem(5);
        }

        .text-container {
          padding-left: rem(5);
          padding-right: $space-15;
          color: var(--text-primary);
          font-size: $font-size-base;
          letter-spacing: 0;
          line-height: rem(18);
          padding-bottom: $space-4;
          max-width: rem(700);
        }
      }
      display: flex;
      flex-direction: column;
      .material-icons {
        font-size: rem(15);
        opacity: 0.5;
        margin-top: rem(3);
      }
    }
    .technical-data {
      padding-top: $space-1;
    }
  }
  .data-sub-title {
    font-size: $font-size-xs;
    color: var(--text-secondary);
    letter-spacing: 0.8px;
    line-height: rem(10);
    text-transform: uppercase;
    margin-bottom: $space-1;
  }
  .data-title {
    color: var(--text-secondary);
    font-size: $font-size-sm;
    letter-spacing: 0.8px;
    line-height: rem(10);
    text-transform: uppercase;
    margin-bottom: rem(15);
  }
  .data-row {
    margin-top: $space-6;
    padding-bottom: $space-9;
    .details-column {
      display: inline-block;
      width: fit-content;
      max-width: rem(650);
      margin-right: $space-4;
    }
    .data-column {
      display: inline-block;
      min-width: rem(90);
      max-width: rem(120);
      margin-right: $space-4;

      .probability {
        line-height: unset;
      }
    }

    .data-column-date {
      display: inline-block;
      width: rem(135);
      margin-right: $space-4;
    }
  }
  .asset-block {
    margin-bottom: $space-5;
  }
  .tld-block {
    margin-bottom: $space-5;
  }
  .scoring-details-column {
    margin-bottom: $space-5;
    display: inline-block;
    width: fit-content;
    max-width: rem(420);
    margin-right: $space-4;
    .data-title {
      color: var(--text-secondary);
      font-size: $font-size-sm;
      letter-spacing: 0.8px;
      line-height: rem(10);
      text-transform: uppercase;
      margin-bottom: $space-1;
    }
  }
  .cvss-score {
    margin-left: $space-6;
    text-align: center;
    font-family: $font-family-semibold;
    font-size: $font-size-base;
    justify-content: center;
    letter-spacing: 0;
    line-height: rem(16);
    padding: rem(5) rem(5) rem(5) rem(6);
  }

  .epss-score {
    margin-top: $space-2;
    align-items: center;
    display: flex;
    max-width: rem(70);
    margin-left: 0;
  }
}
</style>
